import React, { useState } from 'react';
import css from './EditListingLayoutSideNav.module.css';
import { Icons, Button, ExternalLink } from '../../../../components';
import { useSelector } from 'react-redux';
import { userSelector } from '../../../../ducks/user.duck';
import {
  getAssociatedListingId,
  getUserSubscription,
  getUserType,
  hasBrandPremierSubscription,
  isUserTalentPlus,
} from '../../../../util/userDataExtraction';
import { createSlug } from '../../../../util/urlHelpers';
import {
  Direction,
  EditListingTab,
  LinkTalentTabs,
  SUBSCRIPTION_TYPES,
  UserType,
} from '../../../../util/enums';
import { compose } from 'redux';
import { withViewport } from '../../../../util/uiHelpers';
import Drawer from 'react-modern-drawer';

import '../../../../styles/react-modern-drawer.css';
import RenderAccordions from './RenderAccordions';
import { draftId, draftSlug } from '../../../../routing/routeConfiguration';
import { NEW } from '../../../../util/constants';

const EditListingLayoutAccordion = ({ listing, viewport }) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const isMobileLayout = viewport && viewport.width > 0 && viewport.width < 1024;

  const toggleDrawer = () => {
    setIsDrawerOpen(prevState => !prevState);
  };

  const currentUser = useSelector(userSelector);
  const associatedListingId = getAssociatedListingId(currentUser);
  const isTalentPlus = isUserTalentPlus(currentUser);
  const isBrandPremier = hasBrandPremierSubscription(currentUser);
  const userType = getUserType(currentUser);
  const subscriptionType = getUserSubscription(currentUser);
  const isGroupPlus =
    userType === UserType.SCHOOL && subscriptionType === SUBSCRIPTION_TYPES.GROUP_PLUS;
  const listingId = listing?.id?.uuid;
  const listingTitle = listing?.attributes?.title;
  const isAgency = userType === UserType.AGENCY;
  const editListingParams = {
    id: listingId,
    slug: listingTitle && createSlug(listingTitle || ''),
    type: 'edit',
  };

  const commonAccordions = [
    {
      key: 'contact-details',
      label: 'Edit Contact Details',
      isExternal: false,
      pageName: 'ContactDetailsPage',
    },
    {
      key: 'edit-password',
      label: 'Edit Password',
      isExternal: false,
      pageName: 'PasswordChangePage',
    },
    {
      key: 'edit-payout-details',
      label: 'Edit Payout Details',
      isExternal: false,
      pageName: 'StripePayoutPage',
    },
    {
      key: 'payment-methods',
      label: 'Edit Payment Methods',
      isExternal: false,
      pageName: 'PaymentMethodsPage',
    },
  ];

  const nilEdgeZone = {
    id: 'nilEdgeZone',
    title: 'NIL Edge Zone',
    sections: [
      {
        id: 'enhanceYourNil',
        title: 'Enhance Your NIL',
        items: [
          {
            key: 'enroll-academy',
            label: 'Enroll in Prep For NIL Academy',
            isExternal: true,
            pageName: 'https://academy.prepfornil.com',
          },
          {
            key: 'open-a-store',
            label: 'Open A Store',
            isExternal: true,
            pageName: 'http://nilstores.com',
          },
          {
            key: 'shop-promotional-items',
            label: 'Shop Promotional Items',
            isExternal: true,
            pageName: 'https://www.PromosForNIL.com',
          },
          {
            key: 'social-media-growth',
            label: 'Social Media Growth',
            isExternal: false,
            pageName: 'EditListingPage',
            params: { ...editListingParams, tab: EditListingTab.TALENT_PLUS_SERVICES },
          },
          {
            key: 'purchase-autograph-products',
            label: 'Purchase Products to Autograph',
            isExternal: false,
            pageName: 'EditListingPage',
            params: { ...editListingParams, tab: EditListingTab.TALENT_PLUS_SERVICES },
          },
          {
            key: 'electronic-press-kit',
            label: 'Electronic Press Kit',
            isExternal: false,
            pageName: 'EditListingPage',
            params: { ...editListingParams, tab: EditListingTab.TALENT_PLUS_SERVICES },
          },
          {
            key: 'personal-web-design',
            label: 'Personal Web Design',
            isExternal: false,
            pageName: 'EditListingPage',
            params: { ...editListingParams, tab: EditListingTab.TALENT_PLUS_SERVICES },
          },
          {
            key: 'personal-brand-design',
            label: 'Personal Brand Design',
            isExternal: false,
            pageName: 'EditListingPage',
            params: { ...editListingParams, tab: EditListingTab.TALENT_PLUS_SERVICES },
          },
          {
            key: 'custom-3d-replicas',
            label: 'Custom 3D Replicas',
            isExternal: false,
            pageName: 'EditListingPage',
            params: { ...editListingParams, tab: EditListingTab.TALENT_PLUS_SERVICES },
          },
          {
            key: 'custom-mobile-apps',
            label: 'Custom Mobile Apps',
            isExternal: false,
            pageName: 'EditListingPage',
            params: { ...editListingParams, tab: EditListingTab.TALENT_PLUS_SERVICES },
          },
        ],
      },
      {
        id: 'performanceAndHealth',
        title: 'Performance and Health Improvement',
        items: [
          {
            key: 'performance-nutrition',
            label: 'Performance Nutrition',
            isExternal: false,
            pageName: 'EditListingPage',
            params: { ...editListingParams, tab: EditListingTab.TALENT_PLUS_SERVICES },
          },
          {
            key: 'performance-improvement',
            label: 'Performance Improvement',
            isExternal: false,
            pageName: 'EditListingPage',
            params: { ...editListingParams, tab: EditListingTab.TALENT_PLUS_SERVICES },
          },
          {
            key: 'performance-evaluation',
            label: 'Performance Evaluation and Analytics',
            isExternal: false,
            pageName: 'EditListingPage',
            params: { ...editListingParams, tab: EditListingTab.TALENT_PLUS_SERVICES },
          },
          {
            key: 'healthcare-wellness',
            label: 'Healthcare and Wellness',
            isExternal: false,
            pageName: 'EditListingPage',
            params: { ...editListingParams, tab: EditListingTab.TALENT_PLUS_SERVICES },
          },
        ],
      },
      {
        id: 'legalProtection',
        title: 'Legal Protection',
        items: [
          {
            key: 'nil-laws',
            label: 'NIL Laws',
            isExternal: false,
            pageName: 'EditListingPage',
            params: { ...editListingParams, tab: EditListingTab.TALENT_PLUS_SERVICES },
          },
          {
            key: 'identity-protection',
            label: 'Identity Protection',
            isExternal: false,
            pageName: 'EditListingPage',
            params: { ...editListingParams, tab: EditListingTab.TALENT_PLUS_SERVICES },
          },
          {
            key: 'business-formation',
            label: 'Business Formation',
            isExternal: false,
            pageName: 'EditListingPage',
            params: { ...editListingParams, tab: EditListingTab.TALENT_PLUS_SERVICES },
          },
          {
            key: 'legal-support',
            label: 'Legal Support Services',
            isExternal: false,
            pageName: 'LandingPage',
          },
        ],
      },
      {
        id: 'financialGuidance',
        title: 'Financial Guidance',
        items: [
          {
            key: 'financial-advisory',
            label: 'Financial Advisory',
            isExternal: false,
            pageName: 'LandingPage',
          },
          {
            key: 'accounting-tax-services',
            label: 'Accounting and Tax Services',
            isExternal: false,
            pageName: 'LandingPage',
          },
        ],
      },
    ],
  };
  const groupPlusOptions = {
    id: 'groupPlus',
    title: 'Group Plus',
    sections: [
      {
        id: 'create-subscription-plan',
        title: 'Create Subscription Plan',
        isExternal: false,
        hideIcon: true,
        pageName: 'EditListingPage',
        params: { ...editListingParams, tab: EditListingTab.GROUP_PLUS_SERVICES },
      },
      {
        id: 'add-custom-links',
        title: 'Add Custom Links',
        isExternal: false,
        hideIcon: true,
        pageName: 'EditListingPage',
        params: { ...editListingParams, tab: EditListingTab.LINKS },
      },
      {
        id: 'upload-talent',
        title: 'Upload Talent',
        isExternal: false,
        hideIcon: true,
        pageName: 'BulkUploadUsersPage',
        params: { id: listingId },
      },

      {
        id: 'live-streaming-sessions',
        title: 'Live Streaming Sessions',
        isExternal: false,
        hideIcon: true,
        pageName: 'EditListingPage',
        params: { ...editListingParams, tab: EditListingTab.LINKS },
      },
      {
        id: 'fan-chat',
        title: 'Fan Chat',
        isExternal: false,
        hideIcon: true,
        pageName: 'EditListingPage',
        params: { ...editListingParams, tab: EditListingTab.LINKS },
      },
      {
        id: 'campaign-showcase',
        title: 'Campaign Showcase',
        isExternal: false,
        hideIcon: true,
        pageName: 'EditListingPage',
        params: { ...editListingParams, tab: EditListingTab.LINKS },
      },
      {
        id: 'post-content',
        title: 'Post Content',
        isExternal: false,
        hideIcon: true,
        pageName: 'EditListingPage',
        params: { ...editListingParams, tab: EditListingTab.LINKS },
      },
      {
        id: 'integrated-social-media-feeds',
        title: 'Integrated Social Media Feeds',
        isExternal: false,
        hideIcon: true,
        pageName: 'EditListingPage',
        params: { ...editListingParams, tab: EditListingTab.LINKS },
      },
      {
        id: 'aggregate-talent-plus-channels',
        title: 'Aggregate Talent Plus Channels',
        isExternal: false,
        hideIcon: true,
        pageName: 'EditListingPage',
        params: { ...editListingParams, tab: EditListingTab.LINKS },
      },
      {
        id: 'talent-post-feed',
        title: 'Talent Post Feed',
        isExternal: false,
        hideIcon: true,
        pageName: 'EditListingPage',
        params: { ...editListingParams, tab: EditListingTab.LINKS },
      },
      {
        id: 'discounted-rate-education-platform',
        title: 'Discounted Rate for all of your Talent on our Education Platform',
        isExternal: false,
        hideIcon: true,
        pageName: 'EditListingPage',
        params: { ...editListingParams, tab: EditListingTab.LINKS },
      },
      // {
      //   id: 'createAChannel',
      //   title: 'Create a Channel',
      //   items: [
      //     {
      //       key: 'enroll-academy',
      //       label: 'Live Streaming Sessions',
      //       isExternal: true,
      //       pageName: 'http://nilstores.com',
      //     },
      //     {
      //       key: 'fan-chat',
      //       label: 'Fan Chat',
      //       isExternal: true,
      //       pageName: 'http://nilstores.com',
      //     },
      //     {
      //       key: 'social-media-feeds',
      //       label: 'Integrated Social Media Feeds',
      //       isExternal: true,
      //       pageName: 'http://nilstores.com',
      //     },
      //     {
      //       key: 'aggregate-talent-plus',
      //       label: 'Aggregate Talent Plus Channels',
      //       isExternal: true,
      //       pageName: 'http://nilstores.com',
      //     },
      //     {
      //       key: 'talent-post-feed',
      //       label: 'Talent Post Feed',
      //       isExternal: true,
      //       pageName: 'http://nilstores.com',
      //     },
      //     {
      //       key: 'discounted-education-rate',
      //       label: 'Discounted Rate for all of your Talent on our Education Platform',
      //       isExternal: true,
      //       pageName: 'http://nilstores.com',
      //     },
      //   ],
      // },
    ],
  };

  const accordionsByUserType = {
    talent: [
      {
        id: 'yourInfo',
        title: 'Your Info',
        items: [
          {
            key: 'edit-profile',
            label: 'Edit Profile',
            isExternal: false,
            pageName: 'EditListingPage',
            params: { ...editListingParams, tab: EditListingTab.DETAILS },
          },
          {
            key: 'availability',
            label: 'Schedule Availability',
            isExternal: false,
            pageName: 'EditListingPage',
            params: { ...editListingParams, tab: EditListingTab.AVAILABILITY },
          },
          {
            key: 'your-subscription',
            label: 'Your Subscription',
            isExternal: true,
            openInSameTab: true,
            pageName: '/subscription',
          },
          // ...(isTalentPlus
          //   ? [
          //       {
          //         key: 'your-custom-links',
          //         label: 'Your Custom Links',
          //         isExternal: true,
          //         pageName: 'CustomLinksPage',
          //       },
          //     ]
          //   : []),
          ...commonAccordions,
          {
            key: 'your-trading-card',
            label: 'Your Trading Card',
            isExternal: true,
            pageName: 'TradingCardPage',
          },
          {
            key: 'contact-details',
            label: 'Edit Contact Details',
            isExternal: false,
            pageName: 'ContactDetailsPage',
          },
          {
            key: 'payment-methods',
            label: 'Edit Payment Methods',
            isExternal: false,
            pageName: 'PaymentMethodsPage',
          },
          {
            key: 'listing-page',
            label: 'Your Public Profile',
            isExternal: false,
            pageName: 'ListingPage',
            params: { ...editListingParams },
          },
        ],
      },
      {
        id: 'revenueProfile',
        title: 'Your Revenue Profile',
        items: [
          {
            key: 'signature-services',
            label: 'Signature Services',
            isExternal: false,
            pageName: 'EditListingPage',
            params: { ...editListingParams, tab: EditListingTab.SIGNATURE_SERVICES },
          },
          {
            key: 'enhanced-services',
            label: 'Enhanced Services',
            isExternal: false,
            pageName: 'EditListingPage',
            params: { ...editListingParams, tab: EditListingTab.ENHANCED_SERVICES },
          },
          ...(isTalentPlus
            ? [
                {
                  key: 'talent-plus',
                  label: 'Talent Plus',
                  isExternal: false,
                  pageName: 'EditListingPage',
                  params: { ...editListingParams, tab: EditListingTab.TALENT_PLUS_SERVICES },
                },
              ]
            : []),

          {
            key: 'referral-program',
            label: 'Referral Program',
            isExternal: false,
            pageName: 'ReferralPage',
          },
          {
            key: 'marketplace-sales-reports',
            label: 'Marketplace Sales Reports',
            isExternal: true,
            pageName: 'MarketplaceSalesReports',
          },
          {
            key: 'tax-documents',
            label: 'Tax Documents',
            isExternal: true,
            pageName: 'TaxDocumentsPage',
          },
          {
            key: 'your-contracts',
            label: 'Your Contracts',
            isExternal: false,
            pageName: 'LandingPage',
          },
        ],
      },
      {
        id: 'nilOpportunities',
        title: 'Your NIL Opportunities',
        items: [
          {
            key: 'signature-service-requests',
            label: 'Signature Service Requests',
            isExternal: false,
            pageName: 'EditListingPage',
            params: { ...editListingParams, tab: EditListingTab.DETAILS },
          },
          {
            key: 'mentorship-opportunities',
            label: 'Mentorship Opportunities',
            isExternal: false,
            pageName: 'EditListingPage',
            params: { ...editListingParams, tab: EditListingTab.DETAILS },
          },
          {
            key: 'enhanced-services',
            label: 'Enhanced Services',
            isExternal: false,
            pageName: 'EditListingPage',
            params: { ...editListingParams, tab: EditListingTab.ENHANCED_SERVICES },
          },
          ...(isTalentPlus
            ? [
                {
                  key: 'talent-plus',
                  label: 'Talent Plus',
                  isExternal: false,
                  pageName: 'EditListingPage',
                  params: { ...editListingParams, tab: EditListingTab.TALENT_PLUS_SERVICES },
                },
              ]
            : []),
          {
            key: 'custom-requests',
            label: 'Custom Requests',
            isExternal: true,
            pageName: 'CustomRequestsPage',
          },
          {
            key: 'fast-cash-offers',
            label: 'Single Job Offers',
            isExternal: true,
            pageName: 'FastCashOffersPage',
          },
          {
            key: 'bulk-offers',
            label: 'Bulk Job Offers',
            isExternal: true,
            pageName: 'BulkOffersPage',
          },
          {
            key: 'apply-to-campaigns',
            label: 'Apply to Campaigns',
            isExternal: true,
            pageName: 'CampaignApplicationsPage',
          },
          {
            key: 'crowdsourcing',
            label: 'Crowdsourcing',
            isExternal: true,
            pageName: 'CrowdsourcingPage',
          },
          {
            key: 'promote-brand',
            label: 'Promote A Brand on TikTok',
            isExternal: true,
            pageName: 'https://tiktok.com',
          },
          {
            key: 'launch-auction',
            label: 'Launch An Auction',
            isExternal: true,
            pageName: 'AuctionPage',
          },
          {
            key: 'perks',
            label: 'Perks',
            isExternal: true,
            pageName: 'PerksPage',
          },
          {
            key: 'coupons',
            label: 'Rewards',
            isExternal: true,
            pageName: 'CouponsPage',
          },

          {
            key: 'marketplace-perks',
            label: 'Marketplace Perks',
            isExternal: true,
            pageName: 'MarketplacePerksPage',
          },
        ],
      },
      nilEdgeZone,
      // {
      //   id: 'contracts',
      //   title: 'Your Contracts',
      //   items: [
      //     {
      //       key: 'your-contracts',
      //       label: 'View Contracts',
      //       isExternal: false,
      //       pageName: 'LandingPage',
      //     },
      //   ],
      // },
      {
        id: 'inbox',
        title: 'Inbox',
        items: [
          {
            key: 'view-messages',
            label: 'View Messages',
            isExternal: false,
            pageName: 'InboxPage',
            params: { tab: 'sales' },
          },
        ],
      },
      ...(associatedListingId
        ? [
            {
              id: 'association',
              title: 'Association',
              items: [
                {
                  key: 'association',
                  label: 'Association',
                  isExternal: false,
                  pageName: 'AssociatedListingPage',
                  params: { id: associatedListingId },
                },
              ],
            },
          ]
        : []),
    ],
    brand: [
      {
        id: 'yourInfo',
        title: 'Your Info',
        items: [
          {
            key: 'edit-profile',
            label: 'Edit Profile',
            isExternal: false,
            pageName: 'EditListingPage',
            params: { ...editListingParams, tab: EditListingTab.DETAILS },
          },
          {
            key: 'your-subscription',
            label: 'Your Subscription',
            isExternal: true,
            openInSameTab: true,
            pageName: '/subscription',
          },
          {
            key: 'your-lists',
            label: 'Your Lists',
            isExternal: false,
            pageName: 'ListingPage',
            params: { ...editListingParams },
          },
          // {
          //   key: 'your-custom-links',
          //   label: 'Your Custom Links',
          //   isExternal: true,
          //   pageName: 'CustomLinksPage',
          // },
          {
            key: 'contact-details',
            label: 'Edit Contact Details',
            isExternal: false,
            pageName: 'ContactDetailsPage',
          },
          {
            key: 'edit-password',
            label: 'Edit Password',
            isExternal: false,
            pageName: 'PasswordChangePage',
          },
          {
            key: 'payment-methods',
            label: 'Edit Payment Methods',
            isExternal: false,
            pageName: 'PaymentMethodsPage',
          },
          {
            key: 'edit-payout-details',
            label: 'Edit Payout Details',
            isExternal: false,
            pageName: 'StripePayoutPage',
          },
          {
            key: 'listing-page',
            label: 'Your Public Profile',
            isExternal: false,
            pageName: 'ListingPage',
            params: { ...editListingParams },
          },
        ],
      },
      {
        id: 'revenueProfile',
        title: 'Your Revenue Profile',
        items: [
          {
            key: 'referral-program',
            label: 'Referral Program',
            isExternal: false,
            pageName: 'ReferralPage',
          },
          {
            key: 'marketplace-sales-reports',
            label: 'Marketplace Sales Reports',
            isExternal: true,
            pageName: 'MarketplaceSalesReportsPage',
          },
        ],
      },
      {
        id: 'nilOfferings',
        title: 'Your NIL Offerings',
        items: [
          { key: 'coupons', label: 'Rewards', isExternal: true, pageName: 'CouponsPage' },
          { key: 'perks', label: 'Perks', isExternal: true, pageName: 'PerksPage' },
          {
            key: 'fast-cash-offers',
            label: 'Single Job Offers',
            isExternal: true,
            pageName: 'ListingPage',
            params: { ...editListingParams },
          },
          {
            key: 'bulk-offers',
            label: 'Bulk Job Offers',
            isExternal: true,
            pageName: 'ListingPage',
            params: { ...editListingParams },
          },
          {
            key: 'your-campaigns',
            label: 'Your Campaigns',
            isExternal: true,
            pageName: 'ListingPage',
            params: { ...editListingParams },
          },
        ],
      },
      {
        id: 'createPerkOfferCoupon',
        title: 'Create',
        items: [
          {
            key: 'create-a-campaign',
            label: 'Create A Campaign',
            isExternal: false,
            pageName: 'CampaignPostingPage',
            params: {
              slug: draftSlug,
              id: draftId,
              type: NEW,
            },
          },
          {
            key: 'create-a-job',
            label: 'Create A Job',
            isExternal: false,
            pageName: 'JobPostPage',
            params: {
              slug: draftSlug,
              id: draftId,
              type: NEW,
            },
          },
          {
            key: 'create-perk',
            label: 'Create A Perk',
            isExternal: false,
            pageName: 'PerksPostPage',
            params: {
              slug: draftSlug,
              id: draftId,
              type: NEW,
            },
          },
          {
            key: 'create-a-reward',
            label: 'Create A Reward',
            isExternal: false,
            pageName: 'EditRewardsPage',
            params: {
              slug: draftSlug,
              id: draftId,
              type: NEW,
            },
          },
          {
            key: 'offer-a-mentorship',
            label: 'Offer A Mentorship',
            isExternal: false,
            pageName: 'EditListingPage',
            params: { ...editListingParams, tab: EditListingTab.DETAILS },
          },
          {
            key: 'launch-market-research',
            label: 'Launch Market Research',
            isExternal: false,
            pageName: 'EditListingPage',
            params: { ...editListingParams, tab: EditListingTab.DETAILS },
          },
        ],
      },
      ...(isBrandPremier
        ? [
            {
              id: 'brand-premier',
              title: 'Brand Premier',
              items: [
                {
                  key: 'create-subscription',
                  label: 'Create Subscription',
                  isExternal: false,
                  pageName: 'EditListingPage',
                  params: { ...editListingParams, tab: EditListingTab.BRAND_PREMIER_SERVICES },
                },
                {
                  key: 'add-custom-links',
                  label: 'Add Custom Links',
                  isExternal: false,
                  pageName: 'EditListingPage',
                  params: { ...editListingParams, tab: EditListingTab.LINKS },
                },
                {
                  key: 'add-social-media',
                  label: 'Add Social Media Channels',
                  isExternal: false,
                  pageName: 'EditListingPage',
                  params: { ...editListingParams, tab: EditListingTab.BRAND_PREMIER_SERVICES },
                },
                {
                  key: 'add-to-showcase',
                  label: 'Add to Campaign Showcase',
                  isExternal: false,
                  pageName: 'CampaignPostingPage',
                  params: { slug: draftSlug, id: draftId, type: NEW },
                },
                {
                  key: 'post-content',
                  label: 'Post Content',
                  isExternal: false,
                  pageName: 'EditListingPage',
                  params: { ...editListingParams, tab: EditListingTab.BRAND_PREMIER_SERVICES },
                },
              ],
            },
          ]
        : []),
      {
        id: 'nilEdgeZoneForBrand',
        title: 'NIL Edge Zone',
        items: [
          {
            key: 'social-media-growth',
            label: 'Social Media Growth',
            isExternal: false,
            pageName: 'LandingPage',
          },
          {
            key: 'promotional-items',
            label: 'Promotional Items',
            isExternal: false,
            pageName: 'LandingPage',
          },
          {
            key: 'event-planning',
            label: 'Event Planning',
            isExternal: false,
            pageName: 'LandingPage',
          },
          {
            key: 'business-growth-academy',
            label: 'Business Growth Academy',
            isExternal: false,
            pageName: 'LandingPage',
          },
          {
            key: 'reputation-management',
            label: 'Reputation Management',
            isExternal: false,
            pageName: 'LandingPage',
          },
          {
            key: 'tiktok-promotions',
            label: 'TikTok Promotions',
            isExternal: false,
            pageName: 'LandingPage',
          },
          {
            key: 'press-media-outreach',
            label: 'Press & Media Outreach',
            isExternal: false,
            pageName: 'LandingPage',
          },
          {
            key: 'order-print-items',
            label: 'Order Print Items',
            isExternal: false,
            pageName: 'LandingPage',
          },
          {
            key: 'order-design-services',
            label: 'Order Design Services',
            isExternal: false,
            pageName: 'LandingPage',
          },
          {
            key: 'launch-a-store',
            label: 'Launch A Store',
            isExternal: false,
            pageName: 'LandingPage',
          },
          {
            key: 'ppc-and-seo',
            label: 'PPC and SEO',
            isExternal: false,
            pageName: 'LandingPage',
          },
        ],
      },
      {
        id: 'inbox',
        title: 'Inbox',
        items: [
          {
            key: 'view-messages',
            label: 'View Messages',
            isExternal: false,
            pageName: 'InboxPage',
            params: { tab: 'sales' },
          },
        ],
      },
      {
        id: 'associations',
        title: 'Associations',
        items: [
          {
            key: 'link-talent',
            label: 'Link Talent',
            isExternal: false,
            pageName: 'LinkTalentPage',
            params: { tab: LinkTalentTabs.LINK },
          },
          {
            key: 'view-talent',
            label: 'View Talent',
            isExternal: false,
            pageName: 'LinkTalentPage',
            params: { tab: LinkTalentTabs.VIEW },
          },
        ],
      },
    ],
    agency: [
      {
        id: 'yourInfo',
        title: 'Your Info',
        items: [
          {
            key: 'edit-profile',
            label: 'Edit Profile',
            isExternal: false,
            pageName: 'EditListingPage',
            params: { ...editListingParams, tab: EditListingTab.DETAILS },
          },
          {
            key: 'your-subscription',
            label: 'Your Subscription',
            isExternal: true,
            openInSameTab: true,
            pageName: '/subscription',
          },
          {
            key: 'your-lists',
            label: 'Your Lists',
            isExternal: false,
            pageName: 'ListingPage',
            params: { ...editListingParams },
          },
          // {
          //   key: 'your-custom-links',
          //   label: 'Your Custom Links',
          //   isExternal: true,
          //   pageName: 'CustomLinksPage',
          // },
          ...commonAccordions,
          {
            key: 'listing-page',
            label: 'Your Public Profile',
            isExternal: false,
            pageName: 'ListingPage',
            params: { ...editListingParams },
          },
        ],
      },
      {
        id: 'createPerkOfferCoupon',
        title: 'Create',
        items: [
          {
            key: 'create-a-campaign',
            label: 'Create A Campaign',
            isExternal: false,
            pageName: 'CampaignPostingPage',
            params: {
              slug: draftSlug,
              id: draftId,
              type: NEW,
            },
          },
          {
            key: 'create-a-job',
            label: 'Create A Job',
            isExternal: false,
            pageName: 'JobPostPage',
            params: {
              slug: draftSlug,
              id: draftId,
              type: NEW,
            },
          },
          {
            key: 'create-perk',
            label: 'Create A Perk',
            isExternal: false,
            pageName: 'PerksPostPage',
            params: {
              slug: draftSlug,
              id: draftId,
              type: NEW,
            },
          },
          {
            key: 'create-a-reward',
            label: 'Create A Reward',
            isExternal: false,
            pageName: 'EditListingPage',
            params: { ...editListingParams, tab: EditListingTab.DETAILS },
          },
        ],
      },
      ...(subscriptionType === SUBSCRIPTION_TYPES.AGENCY_STANDARD
        ? [
            {
              id: 'agency-standard',
              title: 'Agency Standard',
              items: [
                {
                  key: 'add-custom-links',
                  label: 'Add Custom Links',
                  isExternal: false,
                  pageName: 'EditListingPage',
                  params: { ...editListingParams, tab: EditListingTab.LINKS },
                },
                {
                  key: 'your-talent',
                  label: 'Your Talent',
                  isExternal: false,
                  pageName: 'ListingPage',
                  params: { ...editListingParams },
                },
              ],
            },
          ]
        : []),
      {
        id: 'revenueProfile',
        title: 'Your Revenue Profile',
        items: [
          {
            key: 'your-deals',
            label: 'Your Deals',
            isExternal: false,
            pageName: 'EditListingPage',
            params: { ...editListingParams, tab: EditListingTab.DETAILS },
          },
          {
            key: 'referral-program',
            label: 'Referral Program',
            isExternal: false,
            pageName: 'ReferralPage',
          },
          {
            key: 'marketplace-sales-reports',
            label: 'Marketplace Sales Reports',
            isExternal: true,
            pageName: 'MarketplaceSalesReportsPage',
          },
        ],
      },
      {
        id: 'nilEdgeZone',
        title: 'NIL Edge Zone',
        items: [
          {
            key: 'social-media-growth',
            label: 'Social Media Growth',
            isExternal: false,
            pageName: 'EditListingPage',
            params: { ...editListingParams, tab: EditListingTab.TALENT_PLUS_SERVICES },
          },
          {
            key: 'promotional-items',
            label: 'Promotional Items',
            isExternal: true,
            pageName: 'https://www.PromosForNIL.com',
          },
          {
            key: 'order-print-items',
            label: 'Order Print Items',
            isExternal: false,
            pageName: 'EditListingPage',
            params: { ...editListingParams, tab: EditListingTab.TALENT_PLUS_SERVICES },
          },
          {
            key: 'order-design-services',
            label: 'Order Design Services',
            isExternal: false,
            pageName: 'EditListingPage',
            params: { ...editListingParams, tab: EditListingTab.TALENT_PLUS_SERVICES },
          },
          {
            key: 'launch-a-store',
            label: 'Launch A Store',
            isExternal: false,
            pageName: 'EditListingPage',
            params: { ...editListingParams, tab: EditListingTab.TALENT_PLUS_SERVICES },
          },
        ],
      },
      {
        id: 'inbox',
        title: 'Inbox',
        items: [
          {
            key: 'view-messages',
            label: 'View Messages',
            isExternal: false,
            pageName: 'InboxPage',
            params: { tab: 'sales' },
          },
        ],
      },
      {
        id: 'associations',
        title: 'Associations',
        items: [
          {
            key: 'link-talent',
            label: 'Link Talent',
            isExternal: false,
            pageName: 'LinkTalentPage',
            params: { tab: LinkTalentTabs.LINK },
          },
          {
            key: 'view-talent',
            label: 'View Talent',
            isExternal: false,
            pageName: 'LinkTalentPage',
            params: { tab: LinkTalentTabs.VIEW },
          },

          {
            key: 'upload-talent',
            label: 'Upload Talent',
            isExternal: false,
            pageName: 'BulkUploadUsersPage',
            params: { id: listingId },
          },
        ],
      },
    ],
    school: [
      {
        id: 'yourInfo',
        title: 'Your Info',
        items: [
          {
            key: 'edit-profile',
            label: 'Edit Profile',
            isExternal: false,
            pageName: 'EditListingPage',
            params: { ...editListingParams, tab: EditListingTab.DETAILS },
          },
          {
            key: 'your-subscription',
            label: 'Your Subscription',
            isExternal: true,
            openInSameTab: true,
            pageName: '/subscription',
          },
          ...commonAccordions,
          {
            key: 'listing-page',
            label: 'Your Public Profile',
            isExternal: false,
            pageName: 'ListingPage',
            params: { ...editListingParams },
          },
        ],
      },
      {
        id: 'revenueProfile',
        title: 'Your Revenue Profile',
        items: [
          {
            key: 'referral-program',
            label: 'Referral Program',
            isExternal: false,
            pageName: 'ReferralPage',
          },
          {
            key: 'marketplace-sales-reports',
            label: 'Marketplace Sales Reports',
            isExternal: false,
            pageName: 'EditListingPage',
            params: { ...editListingParams, tab: EditListingTab.DETAILS },
          },
        ],
      },
      ...(isGroupPlus ? [groupPlusOptions] : []),
      {
        id: 'createPerkOfferCoupon',
        title: 'Create',
        items: [
          {
            key: 'create-a-job',
            label: 'Creat A Job',
            isExternal: false,
            pageName: 'JobPostPage',
            params: {
              slug: draftSlug,
              id: draftId,
              type: NEW,
            },
          },
          {
            key: 'create-perk',
            label: 'Create A Perk',
            isExternal: false,
            pageName: 'PerksPostPage',
            params: {
              slug: draftSlug,
              id: draftId,
              type: NEW,
            },
          },
          {
            key: 'create-a-campaign',
            label: 'Create A Campaign',
            isExternal: false,
            pageName: 'CampaignPostingPage',
            params: {
              slug: draftSlug,
              id: draftId,
              type: NEW,
            },
          },
          {
            key: 'create-a-reward',
            label: 'Create A Reward',
            isExternal: false,
            pageName: 'EditListingPage',
            params: { ...editListingParams, tab: EditListingTab.DETAILS },
          },
        ],
      },
      {
        id: 'nilEdgeZone',
        title: 'NIL Edge Zone',
        items: [
          {
            key: 'launch-a-fundraiser',
            label: 'Launch A Fundraiser',
            isExternal: false,
            pageName: 'EditListingPage',
            params: { ...editListingParams, tab: EditListingTab.DETAILS },
          },
          {
            key: 'shop-promotional-items',
            label: 'Shop Promotional Items',
            isExternal: true,
            pageName: 'https://www.PromosForNIL.com',
          },
          {
            key: 'launch-a-store',
            label: 'Launch A Store',
            isExternal: false,
            pageName: 'EditListingPage',
            params: { ...editListingParams, tab: EditListingTab.DETAILS },
          },
          {
            key: 'order-print-items',
            label: 'Order Print Items',
            isExternal: false,
            pageName: 'EditListingPage',
            params: { ...editListingParams, tab: EditListingTab.DETAILS },
          },
          {
            key: 'order-design-services',
            label: 'Order Design Services',
            isExternal: false,
            pageName: 'EditListingPage',
            params: { ...editListingParams, tab: EditListingTab.DETAILS },
          },
        ],
      },
      {
        id: 'inbox',
        title: 'Inbox',
        items: [
          {
            key: 'view-messages',
            label: 'View Messages',
            isExternal: false,
            pageName: 'InboxPage',
            params: { tab: 'sales' },
          },
        ],
      },
      {
        id: 'associations',
        title: 'Associations',
        items: [
          {
            key: 'link-talent',
            label: 'Link Talent',
            isExternal: false,
            pageName: 'LinkTalentPage',
            params: { tab: LinkTalentTabs.LINK },
          },
          {
            key: 'view-talent',
            label: 'View Talent',
            isExternal: false,
            pageName: 'LinkTalentPage',
            params: { tab: LinkTalentTabs.VIEW },
          },
        ],
      },
    ],
    fan: [
      {
        id: 'yourInfo',
        title: 'Your Info',
        items: [
          {
            key: 'edit-profile',
            label: 'Edit Profile',
            isExternal: false,
            pageName: 'EditListingPage',
            params: { ...editListingParams, tab: EditListingTab.DETAILS },
          },
          {
            key: 'contact-details',
            label: 'Edit Contact Details',
            isExternal: false,
            pageName: 'ContactDetailsPage',
          },
          {
            key: 'edit-password',
            label: 'Edit Password',
            isExternal: false,
            pageName: 'PasswordChangePage',
          },
          {
            key: 'edit-payout-details',
            label: 'Edit Payout Details',
            isExternal: false,
            pageName: 'StripePayoutPage',
          },
          {
            key: 'payment-methods',
            label: 'Edit Payment Methods',
            isExternal: false,
            pageName: 'PaymentMethodsPage',
          },
        ],
      },
      {
        id: 'your-lists',
        title: 'Your Lists',
        isLinkTab: true,
        pageName: 'LandingPage',
        items: [],
      },

      {
        id: 'inviteAFriend',
        title: 'Invite A Friend',
        isLinkTab: true,
        pageName: 'LandingPage',
        items: [],
      },
      {
        id: 'yourPurchases',
        title: 'Your Purchases',
        isLinkTab: true,
        items: [],
        pageName: 'LandingPage',
      },
      {
        id: 'auctionBidHistory',
        title: 'Your Auction Bid History',
        isLinkTab: true,
        items: [],
        pageName: 'LandingPage',
      },
      {
        id: 'thunderPoints',
        title: 'Thunder Points',
        isLinkTab: true,
        items: [],
        pageName: 'LandingPage',
      },
      {
        id: 'superfanSubscriptions',
        title: 'Your Superfan Subscriptions',
        isLinkTab: true,
        items: [],
        pageName: 'LandingPage',
      },
      {
        id: 'inbox',
        title: 'Inbox',
        isLinkTab: true,
        pageName: 'InboxPage',
        params: { tab: 'sales' },
        items: [],
      },
    ],
  };

  const accordions = accordionsByUserType[userType] || [];
  return (
    <div className={css.editListingLayoutSideNav}>
      {isMobileLayout ? (
        <>
          <Button className={css.menuButton} type="button" onClick={toggleDrawer}>
            Menu <Icons name="menuVertical" />
          </Button>

          <Drawer
            className={css.editListingLayoutSideNavDrawer}
            open={isDrawerOpen}
            onClose={toggleDrawer}
            direction={Direction.BOTTOM}
          >
            {accordions.map(accordion =>
              accordion.isLinkTab ? (
                <div key={accordion.id} className={css.linkAccordionContainer}>
                  <ExternalLink
                    href={accordion.pageName}
                    className={css.linkAccordion}
                    target={accordion?.openInSameTab ? '_self' : '_blank'}
                  >
                    {accordion.title}
                  </ExternalLink>
                </div>
              ) : (
                <div key={accordion.id}>
                  <RenderAccordions accordions={[accordion]} toggleDrawer={toggleDrawer} />
                  {accordion.subAccordions && accordion.subAccordions.length > 0 && (
                    <div className={css.subAccordionContainer}>
                      {accordion.subAccordions.map(subAccordion => (
                        <div key={subAccordion.id} className={css.subAccordion}>
                          <RenderAccordions accordions={[subAccordion]} />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )
            )}
          </Drawer>
        </>
      ) : (
        accordions.map(accordion =>
          accordion.isLinkTab ? (
            <div key={accordion.id} className={css.linkAccordionContainer}>
              <ExternalLink
                href={accordion.pageName}
                className={css.linkAccordion}
                target={accordion?.openInSameTab ? '_self' : '_blank'}
              >
                {accordion.title}
              </ExternalLink>
            </div>
          ) : (
            <div key={accordion.id}>
              <RenderAccordions accordions={[accordion]} />
              {accordion.subAccordions && accordion.subAccordions.length > 0 && (
                <div className={css.subAccordionContainer}>
                  {accordion.subAccordions.map(subAccordion => (
                    <div key={subAccordion.id} className={css.subAccordion}>
                      <RenderAccordions accordions={[subAccordion]} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )
        )
      )}
    </div>
  );
};

export default compose(withViewport)(EditListingLayoutAccordion);
